// @flow
import {
  call,
  takeEvery,
  put,
  select,
  takeLatest,
  race,
} from 'redux-saga/effects';
import { normalize } from 'normalizr';
import { delay } from 'redux-saga';
import i18n from 'i18n-js';
import isEmpty from 'is-empty-object';
import { customAlphabet } from 'nanoid';
import { change, formValueSelector } from 'redux-form';
import axios from 'axios';

import { throwTimeout } from '../lib/http';
import HTTP_STATUS from '../lib/HTTPStatus';
import { buildErrorStringCreateUser } from '../utils/errorUtils';
import { REQUEST_TIMEOUT, API_BASE_URL } from '../settings';
import {
  USERS_FETCH_STARTED,
  ADD_USER_STARTED,
  START_FETCH_USER_CODE,
  REMOVE_USER_STARTED,
  UPLOAD_USERS_STARTED,
  UPDATE_USER_STARTED,
} from '../types/users';

import {
  DRIVER_RESULT_TYPE,
  MONITOR_RESULT_TYPE,
  PARENT_RESULT_TYPE, STUDENT_RESULT_TYPE,
} from '../components/UserSearchResult/types';
import {
  Parents,
  Students,
  Monitors,
  Drivers,
  Users,
  School,
  api,
} from '../api';
import { arrayOfUsers } from '../api/schemas/users';
import * as selectors from '../reducers';
import * as userActions from '../actions/users';
import * as closeableActions from '../actions/closeable';
import User from '../components/DataTable/Columns/User';
import * as temporalBannersActions from '../actions/temporalBanners';
import * as messages from '../utils/errorDefaults';
import * as routesActions from '../actions/routes';

const nanoid = customAlphabet('1234567890abcdefghijklmnopqrstuvwxyz', 10);

function* fecthUsers(action) {
  const { payload } = action;
  let { page } = payload;
  const debounce = 200;
  const token = yield select(selectors.getToken);
  const profile = yield select(selectors.getProfileOfType, 'admin');
  // const result_type = yield select(selectors.getSelectorSelectedElement('userTypesSelect'));
  // const routeId = yield select(selectors.getSelectorSelectedElement('routeTypesSelect'));
  // result_type = !result_type ? '' : result_type;
  // routeId = !routeId ? '' : routeId;
  const filters = yield select(selectors.getUserFilters);
  filters.result_type = filters.result_type === 'all' ? undefined : filters.result_type;
  page = page == null ? 1 : page;
  yield call(delay, debounce);

  try {
    const { usersResponse, timeout } = yield race({
      usersResponse: call(
        [School.custom, 'users'],
        { id: profile.school, token, filters: { page, page_size: 15, ...filters } },
      ),
      timeout: call(delay, REQUEST_TIMEOUT),
    });

    if (timeout) {
      throwTimeout('fetchUsers saga');
    }
    const fetchedUsers = [];
    usersResponse.results.forEach((element) => {
      element.profiles.forEach(p => fetchedUsers.push({
        ...element, user_id: element.id, id: `${element.id}_${p.id}`, profile_id: p.id, result_type: `${p.type}_profile`,
      }));
    });
    const {
      entities: { users },
      result,
    } = normalize(fetchedUsers, arrayOfUsers);

    yield put(userActions.fetchUsersSucceed(
      users,
      result,
      page,
      usersResponse.next,
    ));
  } catch (error) {
    const {
      statusCode, message, data, isPlain,
    } = error;

    yield put(temporalBannersActions.shout(messages.FETCH_ERROR(i18n.t('common.user', { count: 1 })), messages.DEFAULT_DURATION, -1, {}));
    yield put(userActions.fetchUsersFailed({
      statusCode,
      message,
      data: isPlain ? 'Error en el servidor' : data,
      retryAction: action,
    }));
  }
}

function* uploadUserProfilePicture(action) {
  const { id, file } = action.payload;
  const token = yield select(selectors.getToken);

  const headers = {
    Authorization: token,
    'Content-Type': 'multipart/form-data'
  };
  const data = new FormData();
  data.append('file', file, file.name);

  const response = axios({
    method: 'post',
    url: api.getURL(`users/${id}/set-profile-picture`),
    data,
    headers,
  })
    .then((response) => response.data)
    .catch((error) => { });
}

function* addUser(action) {
  const token = yield select(selectors.getToken);
  const { payload } = action;
  const profile = yield select(selectors.getProfileOfType, 'admin');
  const {
    result_type,
    first_name,
    last_name,
    phone,
    code,
    email,
    license_type,
    address,
    complex_address,
    parents,
    stops_scheduling_going = {},
    stops_scheduling_returning = {},
    isContact,
    profile_picture,
  } = payload; 

  try {
    yield call(delay, 10000);
    const response = {
      id: 1,
      result_type,
      first_name,
      last_name,
      phone,
      code,
      email,
      license_type,
    };

    if (result_type === PARENT_RESULT_TYPE) {
      const { parentResponse, timeout } = yield race({
        parentResponse: call(
          [Parents, 'create'],
          {
            token,
            headers: { 'Content-Type': 'application/json' },
            data: {
              school: profile.school,
              user: {
                first_name,
                last_name,
                address,
                phone: `+502${phone}`,
                email,
              },
              result_type,
            },
          },
        ),
        timeout: call(delay, REQUEST_TIMEOUT),
      });

      if (timeout) {
        throwTimeout('create parent');
      }

      yield put(userActions.addUserSuccess({
        profile_id: parentResponse.id,
        ...parentResponse.user,
        result_type,
        id: `${parentResponse.user.id}_${parentResponse.id}`,
      }));

      if (profile_picture) {
        const payload = {
          id: parentResponse.user.id,
          file: profile_picture.file,
        };
        yield* uploadUserProfilePicture({ payload });
      }

      if (isContact) {
        const formSelector = formValueSelector('createUser');
        const currentParents = yield select(formSelector, 'parents') || [];
        yield put(
          change(
            'createUser',
            'parents',
            [
              ...currentParents,
              {
                ...parentResponse,
                profile_id: parentResponse.id,
                id: parentResponse.user.id,
                email: parentResponse.user.email,
                first_name: parentResponse.user.first_name,
                last_name: parentResponse.user.last_name,
                phone: parentResponse.user.phone,
              },
            ],
          ),
        );
      } else {
        yield put(closeableActions.close('createSingleUser'));
      }
    } else if (result_type === STUDENT_RESULT_TYPE) {
      const studentCode = code !== '' ? code : nanoid();

      // Create student
      const { studentsResponse, timeout } = yield race({
        studentsResponse: call(
          [Students, 'create'],
          {
            token,
            headers: { 'Content-Type': 'application/json' },
            data: {
              school: profile.school,
              code: studentCode,
              user: {
                first_name,
                last_name,
                phone: `+502${phone}`,
                email,
              },
              complex_address,
              result_type,
            },
          },
        ),
        timeout: call(delay, REQUEST_TIMEOUT),
      });

      if (timeout) {
        throwTimeout('create student');
      }

      if (parents && parents.length > 0) {
        for (let i = 0; i < parents.length; i += 1) {
          const idParent = parents[i].profile_id;
          try {
            const { parentsResponse, timeout2 } = yield race({
              parentsResponse: call(
                [Parents.custom, 'addStudent'],
                {
                  token,
                  id: idParent,
                  headers: { 'Content-Type': 'application/json' },
                  data: {
                    school: profile.school,
                    students: [studentsResponse.id],
                  },
                },
              ),
              timeout2: call(delay, REQUEST_TIMEOUT),
            });
            if (timeout2) {
              throwTimeout('create relations students');
            }
          } catch (error) {
            // TODO: DISPATCH ERRROR ASSIGNATION ACTION
          }
        }
      }

      yield put(userActions.addUserSuccess(
        {
          profile_id: studentsResponse.id,
          ...studentsResponse.user,
          id: `${studentsResponse.user.id}_${studentsResponse.id}`,
          result_type,
          profile_picture: (
            studentsResponse.user.profile_picture
              && studentsResponse.user.profile_picture !== null
              ? studentsResponse.user.profile_picture
              : null
          ),
        },
      ));

      const stopsScheduling = {
        ...stops_scheduling_going,
        ...stops_scheduling_returning,
      };

      // User stops assignations
      if (!isEmpty(stopsScheduling)) {
        const stops = Object.keys(stopsScheduling).map(key => stopsScheduling[key]);
        yield put(routesActions.startStudentAssignmentToStops(
          stops,
          studentsResponse.id,
        ));
      }

      if (profile_picture) {
        const payload = {
          id: studentsResponse.user.id,
          file: profile_picture.file,
        };
        yield* uploadUserProfilePicture({ payload });
      }

      yield put(closeableActions.close('createSingleUser'));
    } else if (result_type === MONITOR_RESULT_TYPE) {
      const { monitorsResponse, timeout } = yield race({
        monitorsResponse: call(
          [Monitors, 'create'],
          {
            token,
            headers: { 'Content-Type': 'application/json' },
            data: {
              school: profile.school,
              code,
              user: {
                first_name,
                last_name,
                phone: `+502${phone}`,
                email,
              },
              result_type,

            },
          },
        ),
        timeout: call(delay, REQUEST_TIMEOUT),
      });

      if (timeout) {
        throwTimeout('create student');
      }
      yield put(userActions.addUserSuccess(
        {
          profile_id: monitorsResponse.id,
          ...monitorsResponse.user,
          id: `${monitorsResponse.user.id}_${monitorsResponse.id}`,
          result_type,
          profile_picture: (
            monitorsResponse.user.profile_picture && monitorsResponse.user.profile_picture !== null
              ? monitorsResponse.user.profile_picture
              : null
          ),
        },
      ));

      if (profile_picture) {
        const payload = {
          id: monitorsResponse.user.id,
          file: profile_picture.file,
        };
        yield* uploadUserProfilePicture({ payload });
      }

      yield put(closeableActions.close('createSingleUser'));
    } else if (result_type === DRIVER_RESULT_TYPE) {
      const { driversResponse, timeout } = yield race({
        driversResponse: call(
          [Drivers, 'create'],
          {
            token,
            headers: { 'Content-Type': 'application/json' },
            data: {
              school: profile.school,
              license_type,
              user: {
                first_name,
                last_name,
                phone: `+502${phone}`,
                email,
              },
              result_type,

            },
          },
        ),
        timeout: call(delay, REQUEST_TIMEOUT),
      });

      if (timeout) {
        throwTimeout('create student');
      }

      yield put(userActions.addUserSuccess(
        {
          profile_id: driversResponse.id,
          ...driversResponse.user,
          id: `${driversResponse.user.id}_${driversResponse.id}`,
          result_type,
          profile_picture: (
            driversResponse.user.profile_picture && driversResponse.user.profile_picture !== null
              ? driversResponse.user.profile_picture
              : null
          ),
        },
      ));

      if (profile_picture) {
        const payload = {
          id: driversResponse.user.id,
          file: profile_picture.file,
        };
        yield* uploadUserProfilePicture({ payload });
      }

      yield put(closeableActions.close('createSingleUser'));
    }
    yield put(temporalBannersActions.shout(messages.DEFAULT_CREATE(i18n.t('common.user', { count: 1 })), messages.DEFAULT_DURATION, 1, {}));
  } catch (error) {
    const {
      statusCode, message, data, isPlain,
    } = error;
    const messageStr = buildErrorStringCreateUser(data);
    yield put(userActions.addUserFailed({
      statusCode,
      message: statusCode === HTTP_STATUS.NOT_FOUND ? `Error al crear usuario: ${messageStr}` : messageStr,
      data: isPlain ? 'Error al crear el usuario: \n' : messageStr,
      retryAction: action,
    }));
  }
}

function* uploadUsers(action) {
  const { payload } = action;
  const { users } = payload || { users: [] };
  const currentUsers = [];

  users.forEach((user) => {
    const newUser = {
      first_name: user.nombre,
      last_name: user.apellido,
      result_type: `${user.tipo}_profile`,
      phone: user.telefono,
      email: user.email,
      complete: user.complete,
    };

    return newUser.complete && currentUsers.push(newUser);
  });

  // const token = yield select(selectors.getToken);

  try {
    yield call(delay, 6000);
    /*
    const { response, timeout } = yield race({
      response: call(
        [ Users, 'upload' ],
        {
          token,
          headers:{ 'Content-Type': 'application/json' },
          data:{
            users
          }
        }
      ), timeout: call(delay, REQUEST_TIMEOUT)
    });

    if (timeout) {
      throwTimeout('create user');
    }
    */
    yield put(userActions.clearUploadingUsers());
    yield put(closeableActions.close('uploadUsers'));
    yield put(userActions.uploadUsersSuccess());
    yield put(userActions.startFetchingUsers(1));
  } catch (error) { /* noop */ }
}

function* removeUser(action) {
  const { orderId, id, result_type } = action.payload;
  const token = yield select(selectors.getToken);
  const profile = yield select(selectors.getProfileOfType, 'admin');
  const posibilities = {
    parent_profile: Parents,
    student_profile: Students,
    monitor_profile: Monitors,
    driver_profile: Drivers,
  };
  try {
    const { timeout } = yield race({
      response: call(
        [posibilities[result_type] || User, 'remove'],
        { token, id, data: { school: profile.school } },
      ),
      timeout: call(delay, REQUEST_TIMEOUT),
    });
    if (timeout) {
      throwTimeout('delete user saga');
    }
    yield put(temporalBannersActions.shout(messages.DEFAULT_REMOVE(i18n.t('common.user', { count: 1 })), messages.DEFAULT_DURATION, 1, {}));
    yield put(userActions.removeUserSuccess(orderId));
  } catch (error) {
    const {
      statusCode, message, data, isPlain,
    } = error;
    yield put(temporalBannersActions.shout(messages.REMOVE_ERROR(i18n.t('common.user', { count: 1 })), messages.DEFAULT_DURATION, -1, {}));
    yield put(userActions.removeUserFailed({
      statusCode,
      message,
      data: isPlain ? 'Error en el servidor' : data,
      retryAction: action,
    }));
  }
}

function* updateUser(action) {
  // const oldUser = yield select(selectors.getOneUser, action.payload.id);
  // yield put(userActions.updateUserSucceed(action.payload));
  const profile = yield select(selectors.getProfileOfType, 'admin');
  const token = yield select(selectors.getToken);
  try {
    // Update  route
    const { usersResponse, timeout } = yield race({
      usersResponse: call(
        [Users, 'update'],
        {
          token,
          id: action.payload.user_id,
          data: {
            ...action.payload,
            school: profile.school,
            profile_picture: undefined,
          },
        },
      ),
      timeout: call(delay, REQUEST_TIMEOUT),
    });

    if (timeout) {
      throwTimeout('updateUser saga');
    }
    yield put(temporalBannersActions.shout(messages.DEFAULT_UPDATE(i18n.t('common.user', { count: 1 })), 3000, 1, {}));
    yield put(userActions.updateUserSucceed({
      ...usersResponse,
      profile_picture: usersResponse.profile_picture === null
        ? null
        : usersResponse.profile_picture.url,
      id: `${usersResponse.id}_${usersResponse.profiles[0].id}`,
    }));
  } catch (error) {
    // Set error
    const {
      statusCode,
      message,
      data,
      isPlain,
    } = error;
    yield put(temporalBannersActions.shout(messages.UPDATE_ERROR(i18n.t('common.user', { count: 1 })), messages.DEFAULT_DURATION, -1, {}));
    // yield put(userActions.updateUserSucceed(oldUser));
    yield put(userActions.updateUserFailed({
      statusCode,
      message: statusCode === HTTP_STATUS.NOT_FOUND ? 'Error al update de user' : message,
      data: isPlain ? 'Error en el servidor' : data,
      retryAction: action,
    }));
  }
}

function* getUserCode(action) {
  const { id, stateUserId } = action.payload;
  const token = yield select(selectors.getToken);
  const user = yield select(selectors.getOneUser, stateUserId);

  try {
    const { response, timeout } = yield race({
      response: call(
        [Users.custom, 'getCode'],
        { token, id },
      ),
      timeout: call(delay, REQUEST_TIMEOUT),
    });
    if (timeout) {
      throwTimeout('get user code saga');
    }
    const { user_token } = response;
    user.token = user_token;

    yield put(userActions.updateUserSucceed(user));
  } catch (error) {
    yield put(temporalBannersActions.shout(i18n.t('temporalBanner.accessCodeError'), messages.DEFAULT_DURATION, -1, {}));
    const {
      statusCode, message, data, isPlain,
    } = error;
    yield put(userActions.removeUserFailed({
      statusCode,
      message,
      data: isPlain ? 'Error en el servidor' : data,
      retryAction: action,
    }));
  }
}


export function* watchUsersFetch(): Iterator<any> {
  yield takeLatest(
    USERS_FETCH_STARTED,
    fecthUsers,
  );
}

export function* watchAddUser(): Iterator<any> {
  yield takeEvery(
    ADD_USER_STARTED,
    addUser,
  );
}

export function* watchRemoveUser(): Iterator<any> {
  yield takeEvery(
    REMOVE_USER_STARTED,
    removeUser,
  );
}

export function* watchUpdateUser(): Iterator<any> {
  yield takeLatest(
    UPDATE_USER_STARTED,
    updateUser,
  );
}

export function* watchUploadUsers(): Iterator<any> {
  yield takeEvery(
    UPLOAD_USERS_STARTED,
    uploadUsers,
  );
}

export function* watchFetchUserCode(): Iterator<any> {
  yield takeEvery(
    START_FETCH_USER_CODE,
    getUserCode,
  );
}



// WEBPACK FOOTER //
// ./src/scripts/sagas/users.js